import type { CallOffPublicView } from '../types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasPublicApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodCallOffPublicViewSchema } from './validations';

/**
 * Fetches a single call-off by ID for an external user using a magic link token.
 * This is a public endpoint that doesn't require authentication - the token
 * is passed as a query parameter.
 *
 * @param callOffId - The UUID of the call-off to fetch.
 * @param token - The magic link token for authorization.
 * @param options - Additional request options.
 * @returns The public call-off data (limited fields) including line items.
 * @throws Error if the API request fails.
 * @see https://add.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link-(Public)/operation/get_calloff_via_magic_link_calloffs_magic_link__calloff_id__get
 */
export const fetchCallOffViaMagicLink = async (
  callOffId: UUID,
  token: string,
  options?: { signal?: AbortSignal },
): Promise<CallOffPublicView> => {
  const response = await vestigasPublicApi
    .get(ENDPOINT.CALL_OFF.GET_VIA_MAGIC_LINK(callOffId), {
      searchParams: { token },
      signal: options?.signal,
    })
    .json<CallOffPublicView>();

  // Validate the response with Zod
  const validatedResponse = zodCallOffPublicViewSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'CallOff Magic Link API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
